import React, { useContext } from 'react';
import { Desc, t } from '@sm/intl';
import { generateMetricsAttribute } from '@sm/metrics';
import { StaticContext } from '@sm/webassets/StaticContext';
import { createURL, getClientEnvironmentDetails } from '@sm/utils';
import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import classNames from 'classnames';
import { Button } from '~app/components/core/Button';

import useStyles from './useStyles';
import LogoWall from '../LogoWall';
import g2RatingDesktop from '~static/images/driveGetResults2025/g2RatingDesktop.webp';
import g2RatingMobile from '~static/images/driveGetResults2025/g2RatingMobile.webp';
import { getSrc } from '~app/helpers/assetLoading';
import getVariantClass from '~app/helpers/endpage/getVariantClass';

export type ColorProp = 'sabaeus' | 'canvas' | 'vine' | 'white' | 'greyOil';

type CopyTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div';

export type Props = {
  copy: string;
  copyTag?: CopyTag;
  ctaText?: Desc;
  ctaUsage?: string;
  color?: ColorProp;
  utSource: string;
  utSource2: string;
  hideCTAs?: boolean;
  showLogoWall?: boolean;
  route?: string;
  variant?: string;
};

const FortuneCallout = ({
  color = 'canvas',
  copy,
  copyTag = 'h3',
  ctaText,
  ctaUsage,
  utSource,
  utSource2,
  hideCTAs = false,
  showLogoWall = false,
  route = '/user/sign-up/',
  variant,
}: Props): React.ReactElement => {
  const classes = useStyles({ color, showLogoWall });
  const utSource3 = 'FortuneCallout';
  const {
    smParams: { surveyId, collectorId, respondentId },
    templateType,
  } = useContext(StaticContext);

  const renderDriveGetResults2025g2Ratings = (): JSX.Element => {
    const { isMobile } = getClientEnvironmentDetails();
    const imageAsset = isMobile ? g2RatingMobile : g2RatingDesktop;
    return (
      <div className={getVariantClass(classes, variant, 'G2RatingsWrapper')}>
        <img sizes="(max-width: 375px) 200px, 250px" src={getSrc(imageAsset)} alt="G2 Ratings of SurveyMonkey" />
        <Typography component="p" align="center" color="light">
          14,000+ 5-star reviews on G2.com
        </Typography>
      </div>
    );
  };

  const shouldShowDriveGetResults = variant === 'driveGetResults2025Variant2';
  const driveGetResults2CopyText = 'See why 42M+ people choose SurveyMonkey';

  // Determine the class to use based on the variant and suffix
  const resolverVariantClass = (suffix: string, fallback = ''): string =>
    getVariantClass(classes, variant, suffix, fallback);

  return (
    <Box
      data-testid="FortuneCallout"
      display="flex"
      flexAlign="center"
      flexDirection="column"
      className={classNames(classes.container, resolverVariantClass('Container'))}
      pb={8}
    >
      <Box className={resolverVariantClass('BoxSection')} pt={7} pb={5} pr={3} pl={3}>
        <Typography component={copyTag} align="center" variant="sectionTitleSm">
          <span className={`${classes.copyText} ${resolverVariantClass('CopyText')}`}>
            {shouldShowDriveGetResults ? driveGetResults2CopyText : copy}
          </span>
        </Typography>
        {shouldShowDriveGetResults && renderDriveGetResults2025g2Ratings()}
      </Box>

      {!shouldShowDriveGetResults && showLogoWall && (
        <LogoWall
          darkBackground={color === 'sabaeus' || color === 'vine' || color === 'greyOil'}
          utSource2={utSource2}
        />
      )}
      {!hideCTAs && ctaText && ctaUsage && (
        <Box mt={5}>
          <Button
            className={resolverVariantClass('Button', 'button')}
            variant={shouldShowDriveGetResults ? 'secondary' : undefined}
            color={shouldShowDriveGetResults ? 'secondary' : undefined}
            onDark={shouldShowDriveGetResults ? true : undefined}
            size="lg"
            href={createURL(route, {
              ut_source: utSource,
              ut_source2: utSource2,
              ut_source3: utSource3,
              ut_ctatext: ctaText.defaultMessage ?? '',
            })}
            data-sm-metrics={generateMetricsAttribute({
              data: {
                amplitudeEvent: 'endpage user interaction',
                itemSelected: ctaUsage,
                templateType,
                surveyId,
                collectorId,
                respondentId,
                log: {
                  message: `${utSource}-${utSource2}-${utSource3}-${ctaText.defaultMessage}`,
                },
              },
            })}
          >
            {t(ctaText)}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default FortuneCallout;
